import { toWholeFrancs } from './format'

/**
 * LA CASCADE MENSUELLE.
 *
 * Fonctions pures, hors de tout composant : elles portent des chiffres
 * d'argent, donc elles se testent.
 *
 * L'ordre vient de votre cahier des charges :
 *   1. total des loyers encaisses
 *   2. deductions recurrentes, dans leur ordre configure
 *      (honoraires de gerance, versement a la mere, service de menage)
 *   3. depenses approuvees uniquement
 *   4. net a verser
 *
 * Rien d'autre ne reduit le net. Toute autre somme doit passer par une
 * depense approuvee, ou par une charge recurrente creee explicitement.
 */

export type RecurringKind = 'pourcentage' | 'fixe'

export interface RecurringCharge {
  code: string
  label: string
  kind: RecurringKind
  rate: number | null
  fixed_amount: number | null
  base: string | null
  payee: string | null
  requires_receipt: boolean
  sort_order: number
  a_verifier: boolean
}

export interface WaterfallLine {
  code: string
  label: string
  kind: RecurringKind
  /** Taux applique, pour un pourcentage. */
  rateUsed: number | null
  /** Base a laquelle le taux s'est applique. */
  baseAmount: number | null
  amount: number
  payee: string | null
  requiresReceipt: boolean
  aVerifier: boolean
}

export interface Waterfall {
  rentCollected: number
  lines: WaterfallLine[]
  recurringTotal: number
  expensesApproved: number
  netToDeposit: number
}

/**
 * Calcule la cascade d'un mois.
 *
 * Les pourcentages s'appliquent aux loyers ENCAISSES, jamais aux
 * loyers attendus : on ne prend pas d'honoraires sur un loyer qui
 * n'est pas rentre. La feuille de juillet 2026 le confirme, 8 % de
 * 1 719 000 donnant les 137 520 inscrits.
 *
 * Arrondi au franc entier, au plus proche : il n'y a pas de centime de
 * franc CFA.
 */
export function computeWaterfall(
  rentCollected: number,
  charges: RecurringCharge[],
  expensesApproved: number,
): Waterfall {
  const lines: WaterfallLine[] = [...charges]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((c) => {
      if (c.kind === 'pourcentage') {
        const taux = c.rate ?? 0
        return {
          code: c.code,
          label: c.label,
          kind: c.kind,
          rateUsed: taux,
          baseAmount: rentCollected,
          amount: toWholeFrancs(rentCollected * taux),
          payee: c.payee,
          requiresReceipt: c.requires_receipt,
          aVerifier: c.a_verifier,
        }
      }
      return {
        code: c.code,
        label: c.label,
        kind: c.kind,
        rateUsed: null,
        baseAmount: null,
        amount: c.fixed_amount ?? 0,
        payee: c.payee,
        requiresReceipt: c.requires_receipt,
        aVerifier: c.a_verifier,
      }
    })

  const recurringTotal = lines.reduce((s, l) => s + l.amount, 0)

  return {
    rentCollected,
    lines,
    recurringTotal,
    expensesApproved,
    // Le net peut etre negatif un mois de gros travaux : on ne le
    // ramene pas a zero, cela masquerait le fait que le mois coute
    // plus qu'il ne rapporte.
    netToDeposit: rentCollected - recurringTotal - expensesApproved,
  }
}

/**
 * Charges en vigueur pour un mois donne.
 *
 * Une charge s'applique si le mois tombe dans sa periode de validite.
 * C'est ce qui permet de changer un taux sans reecrire les mois deja
 * calcules : l'ancien mois retrouve l'ancien taux.
 */
export function chargesInForce<
  T extends { effective_from: string; effective_to: string | null; active: boolean },
>(charges: T[], period: string): T[] {
  // Le mois est represente par son premier jour, le 5.
  const jour = `${period}-05`
  return charges.filter(
    (c) =>
      c.active &&
      c.effective_from <= jour &&
      (c.effective_to === null || c.effective_to > jour),
  )
}

/**
 * Repartition du net entre les deux comptes, selon la regle active.
 *
 * Si le net est inferieur au montant fixe, le compte fixe prend tout
 * et l'autre recoit zero. C'etait une question ouverte ; c'est la
 * seule lecture qui ne cree pas de montant negatif.
 */
export function splitNet(
  net: number,
  fixedAmount: number,
): { fixed: number; remainder: number } {
  if (net <= 0) return { fixed: 0, remainder: 0 }
  const fixed = Math.min(net, fixedAmount)
  return { fixed, remainder: net - fixed }
}
